import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  AttachFile,
  MoreVert,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import AppointmentDetailView from "./appointmentDetailView";
import { useNavigate } from "react-router-dom";
import ChatModal from "../chatModal";
import { supabase } from "../../../supabaseClient";
import AppointmentRow from "./appointmentRow"; // Import the updated AppointmentRow component
import { authenticatedFetch } from "../../../network/api";

const ScheduledView = ({
  searchQuery = "",
  showOnlyUpdates = false,
  selectedFilters = {},
  appointments = [],
  loading = false,
  error = null,
  onRefresh,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: "requestedTimestamp",
    direction: "asc",
  });
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [intakeStatus, setIntakeStatus] = useState({}); // Track intake status
  const [hasFormMap, setHasFormMap] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    if (appointments.length === 0) return;

    // 1) grab just the IDs you need
    const rawIds = appointments.map((a) => a.appointment_types_id);

    // 2) de-dupe them
    const uniqueIds = Array.from(new Set(rawIds));

    // 3) seed your map so everyone starts out hidden
    const initialMap = uniqueIds.reduce((map, id) => {
      map[id] = true;
      return map;
    }, {});
    setHasFormMap(initialMap);

    // 4) fire off your batch-lookup
    authenticatedFetch("/api/attorney/webapp/intake/forms/checkForm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ appointment_ids: uniqueIds }),
    })
      .then((res) => res.json())
      .then(({ hasFormMap }) => {
        if (hasFormMap) setHasFormMap(hasFormMap);
      })
      .catch((err) => {
        console.error("Failed to load intake-form flags", err);
      });
  }, [appointments]);

  // Set up Supabase real-time subscription for intake notifications
  useEffect(() => {
    // Subscribe to intake form changes
    const channel = supabase.channel("intake_notifications");

    channel
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "intake_responses", // Adjust table name as needed
        },
        (payload) => {
          console.log("Intake form updated:", payload);

          // Update intake status based on the change
          if (payload.new) {
            setIntakeStatus((prev) => ({
              ...prev,
              [payload.new.appointment_id]: {
                status: payload.new.status,
                submitted_at: payload.new.submitted_at,
              },
            }));
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  // Poll for intake status updates
  useEffect(() => {
    const checkIntakeStatus = async () => {
      if (appointments.length === 0) return;

      try {
        const appointmentIds = appointments.map((apt) => apt.id);

        const { data, error } = await supabase
          .from("intake_responses")
          .select("appointment_id, status, submitted_at")
          .in("appointment_id", appointmentIds);

        if (error) {
          console.error("Error fetching intake status:", error);
          return;
        }

        const statusMap = {};
        data?.forEach((intake) => {
          statusMap[intake.appointment_id] = {
            status: intake.status,
            submitted_at: intake.submitted_at,
          };
        });

        setIntakeStatus(statusMap);
      } catch (error) {
        console.error("Error in checkIntakeStatus:", error);
      }
    };

    // Initial check
    checkIntakeStatus();

    // Set up polling interval
    const interval = setInterval(checkIntakeStatus, 30000);

    return () => clearInterval(interval);
  }, [appointments]);

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const handleViewDetail = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleMenuClick = (event, appointmentId) => {
    setAnchorEl(event.currentTarget);
    setActionMenuOpen(appointmentId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionMenuOpen(null);
  };

  const toggleExpandedRow = (appointmentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(appointmentId)) {
      newExpanded.delete(appointmentId);
    } else {
      newExpanded.add(appointmentId);
    }
    setExpandedRows(newExpanded);
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const filteredAndSortedAppointments = useMemo(() => {
    let filtered = appointments.filter((appointment) => {
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const clientName = `${appointment.client?.first_name || ""} ${
          appointment.client?.last_name || ""
        }`.toLowerCase();
        const appointmentType =
          appointment.appointmentType?.name?.toLowerCase() || "";
        const message = appointment.message?.toLowerCase() || "";

        if (
          !clientName.includes(searchLower) &&
          !appointmentType.includes(searchLower) &&
          !message.includes(searchLower)
        ) {
          return false;
        }
      }

      if (showOnlyUpdates && !appointment.ai_recommendation) {
        return false;
      }

      if (
        selectedFilters["Urgent"] &&
        !appointment.ai_reason?.includes("urgent")
      ) {
        return false;
      }

      if (
        selectedFilters["Has Document"] &&
        (!appointment.documents || appointment.documents.length === 0)
      ) {
        return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case "clientName":
          aValue = `${a.client?.first_name || ""} ${a.client?.last_name || ""}`;
          bValue = `${b.client?.first_name || ""} ${b.client?.last_name || ""}`;
          break;
        case "requestedTimestamp":
          aValue = new Date(a.requestedTimestamp);
          bValue = new Date(b.requestedTimestamp);
          break;
        case "appointmentType":
          aValue = a.appointmentType?.appointment_name || "";
          bValue = b.appointmentType?.appointment_name || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [appointments, searchQuery, showOnlyUpdates, selectedFilters, sortConfig]);

  const SortableTableCell = ({ column, children, sx, ...props }) => (
    <TableCell
      {...props}
      onClick={() => handleSort(column)}
      sx={{
        cursor: "pointer",
        "&:hover": { backgroundColor: "grey.50" },
        fontFamily: "Poppins",
        fontSize: "16px",
        fontWeight: 500,
        color: "#333333",
        textTransform: "uppercase",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {children}
        {sortConfig.key === column &&
          (sortConfig.direction === "asc" ? <ExpandLess /> : <ExpandMore />)}
      </Box>
    </TableCell>
  );

  // Pass intake status to detail view
  if (selectedAppointment) {
    return (
      <AppointmentDetailView
        appointment={selectedAppointment}
        appointments={filteredAndSortedAppointments}
        onBack={() => setSelectedAppointment(null)}
        // onAccept={handleAccept}
        // onReject={handleReject}
        intakeStatus={intakeStatus} // Pass intake status
        onAppointmentUpdate={(updatedAppointment) => {
          // Handle appointment updates from detail view
          onRefresh();
        }}
      />
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography color="error" variant="h6" gutterBottom>
          Error loading appointments
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={onRefresh} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (filteredAndSortedAppointments.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h5" gutterBottom>
          No scheduled consultations yet{" "}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ maxWidth: "500px", mx: "auto", mb: 3 }}
        >
          You haven't confirmed any upcoming consultations. Once you accept a
          client request, it will appear here — along with any documents, AI
          summaries, and intake information to help you prepare.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          . Looking for past meetings? View them in your{" "}
          <Button
            onClick={() => {
              navigate("/attorney/calendar");
            }}
            variant="text"
            size="small"
          >
            calendar
          </Button>
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <SortableTableCell
                column="clientName"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                }}
              >
                Client Name
              </SortableTableCell>
              <SortableTableCell
                column="created_at"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                }}
              >
                Submitted updated
              </SortableTableCell>
              <SortableTableCell
                column="requestedTimestamp"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                }}
              >
                Appointment Time
              </SortableTableCell>
              <TableCell
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                  textTransform: "uppercase",
                }}
              >
                Consultation Type
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                  textTransform: "uppercase",
                }}
              >
                Intake Form
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                  textTransform: "uppercase",
                }}
              >
                Files
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                  textTransform: "uppercase",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredAndSortedAppointments.map((appointment) => (
              <AppointmentRow
                key={appointment.id}
                appointment={appointment}
                isExpanded={expandedRows.has(appointment.id)}
                onToggleExpand={toggleExpandedRow}
                onViewDetail={handleViewDetail}
                formatDate={formatDate}
                formatTime={formatTime}
                getTimeAgo={getTimeAgo}
                intakeStatus={intakeStatus[appointment.id]} // Pass intake status for this appointment
                hasForm={!!hasFormMap[appointment.appointment_types_id]}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ScheduledView;
